import React, { createContext, useContext, useReducer, useCallback } from "react";
import { ACTION_TYPES, CONVERSATION_FILTERS } from "../utils/constants";

/**
 * Estado inicial da Central de Atendimento
 */
const initialState = {
  // Contas WhatsApp
  accounts: [],
  selectedAccount: null,
  accountProfilePicture: null,

  // Conversas
  conversations: [],
  selectedConversation: null,
  conversationsOffset: 0,
  hasMoreConversations: true,

  // Filtros
  filters: {
    status: CONVERSATION_FILTERS.STATUS.ALL,
    attendant: CONVERSATION_FILTERS.ATTENDANT.ALL,
    department: null,
    search: "",
  },

  // Filtro de canais (omnichannel) — Set vazio = "Todos os canais".
  // Persistido em localStorage por usuário.
  selectedChannels: (() => {
    try {
      const raw = localStorage.getItem("sc_selected_channels");
      const arr = raw ? JSON.parse(raw) : [];
      return new Set(Array.isArray(arr) ? arr : []);
    } catch {
      return new Set();
    }
  })(),

  // Contato selecionado (dados completos do backend)
  selectedContact: null,

  // Mensagens
  messages: [],
  messagesOffset: 0,
  hasMoreMessages: true,

  // Notas do contato
  notes: [],

  // Sessões do contato
  sessions: [],
  currentSession: null,

  // Contadores de não lidas
  unreadCounts: {},

  // UI State
  isContactPanelOpen: true,
  isMobile: false,
  showSidebar: true,

  // Loading states
  loading: {
    accounts: false,
    conversations: false,
    messages: false,
    contact: false,
    notes: false,
    sessions: false,
    sending: false,
  },

  // Erros
  errors: {},
};

/**
 * Reducer para gerenciar estado
 */
const serviceCenterReducer = (state, action) => {
  switch (action.type) {
    // ==========================================
    // CONTAS
    // ==========================================
    case ACTION_TYPES.SET_ACCOUNTS:
      return {
        ...state,
        accounts: action.payload,
      };

    case ACTION_TYPES.SET_SELECTED_ACCOUNT:
      return {
        ...state,
        selectedAccount: action.payload.account,
        accountProfilePicture: action.payload.profilePicture || null,
        // Reset ao mudar de conta
        conversations: [],
        selectedConversation: null,
        selectedContact: null,
        messages: [],
        notes: [],
        sessions: [],
        conversationsOffset: 0,
        hasMoreConversations: true,
      };

    // ==========================================
    // CONVERSAS
    // ==========================================
    case ACTION_TYPES.SET_CONVERSATIONS:
      return {
        ...state,
        conversations: action.payload.conversations,
        conversationsOffset: action.payload.offset || 0,
        hasMoreConversations: action.payload.hasMore !== false,
      };

    case ACTION_TYPES.ADD_CONVERSATIONS:
      return {
        ...state,
        conversations: [
          ...state.conversations,
          ...action.payload.conversations.filter(
            (newConv) =>
              !state.conversations.some((existing) => existing.id === newConv.id)
          ),
        ],
        conversationsOffset: action.payload.offset,
        hasMoreConversations: action.payload.hasMore !== false,
      };

    case ACTION_TYPES.UPDATE_CONVERSATION: {
      const updatedConversation = action.payload;
      return {
        ...state,
        conversations: state.conversations.map((conv) =>
          conv.id === updatedConversation.id
            ? { ...conv, ...updatedConversation }
            : conv
        ),
        selectedConversation:
          state.selectedConversation?.id === updatedConversation.id
            ? { ...state.selectedConversation, ...updatedConversation }
            : state.selectedConversation,
      };
    }

    case ACTION_TYPES.SET_SELECTED_CONVERSATION:
      return {
        ...state,
        selectedConversation: action.payload,
        // Reset mensagens ao mudar conversa
        messages: [],
        messagesOffset: 0,
        hasMoreMessages: true,
        // Reset notas e sessões
        notes: [],
        sessions: [],
        currentSession: null,
      };

    // ==========================================
    // CONTATO
    // ==========================================
    case ACTION_TYPES.SET_SELECTED_CONTACT:
      return {
        ...state,
        selectedContact: action.payload,
      };

    case ACTION_TYPES.UPDATE_CONTACT: {
      const updatedContact = action.payload;
      return {
        ...state,
        selectedContact:
          state.selectedContact?.id === updatedContact.id
            ? { ...state.selectedContact, ...updatedContact }
            : state.selectedContact,
        // Atualizar também a conversa correspondente
        conversations: state.conversations.map((conv) =>
          conv.contact_id === updatedContact.id
            ? {
                ...conv,
                service_status: updatedContact.service_status,
                current_attendant_id: updatedContact.current_attendant,
                current_attendant_name: updatedContact.current_attendant_name,
                current_department_id: updatedContact.current_department,
                current_department_name: updatedContact.current_department_name,
                bot_status: updatedContact.bot_status,
              }
            : conv
        ),
        selectedConversation:
          state.selectedConversation?.contact_id === updatedContact.id
            ? {
                ...state.selectedConversation,
                service_status: updatedContact.service_status,
                current_attendant_id: updatedContact.current_attendant,
                current_attendant_name: updatedContact.current_attendant_name,
                current_department_id: updatedContact.current_department,
                current_department_name: updatedContact.current_department_name,
                bot_status: updatedContact.bot_status,
              }
            : state.selectedConversation,
      };
    }

    // ==========================================
    // MENSAGENS
    // ==========================================
    case ACTION_TYPES.SET_MESSAGES:
      return {
        ...state,
        messages: action.payload.messages,
        messagesOffset: action.payload.offset || 0,
        hasMoreMessages: action.payload.hasMore !== false,
      };

    case ACTION_TYPES.ADD_MESSAGES:
      // Adiciona mensagens antigas no início (load more)
      return {
        ...state,
        messages: [...action.payload.messages, ...state.messages],
        messagesOffset: action.payload.offset,
        hasMoreMessages: action.payload.hasMore !== false,
      };

    case ACTION_TYPES.ADD_MESSAGE:
      // Adiciona nova mensagem no final (tempo real)
      return {
        ...state,
        messages: [...state.messages, ...action.payload],
      };

    case ACTION_TYPES.UPDATE_MESSAGE: {
      const { messageId, updates } = action.payload;
      return {
        ...state,
        messages: state.messages.map((msg) =>
          msg.id === messageId || msg.timestamp === messageId
            ? { ...msg, ...updates }
            : msg
        ),
      };
    }

    // ==========================================
    // NOTAS
    // ==========================================
    case ACTION_TYPES.SET_NOTES:
      return {
        ...state,
        notes: action.payload,
      };

    case ACTION_TYPES.ADD_NOTE:
      return {
        ...state,
        notes: [action.payload, ...state.notes],
      };

    case ACTION_TYPES.UPDATE_NOTE: {
      const updatedNote = action.payload;
      return {
        ...state,
        notes: state.notes.map((note) =>
          note.id === updatedNote.id ? { ...note, ...updatedNote } : note
        ),
      };
    }

    case ACTION_TYPES.DELETE_NOTE:
      return {
        ...state,
        notes: state.notes.filter((note) => note.id !== action.payload),
      };

    // ==========================================
    // SESSÕES
    // ==========================================
    case ACTION_TYPES.SET_SESSIONS:
      return {
        ...state,
        sessions: action.payload,
      };

    case ACTION_TYPES.SET_CURRENT_SESSION:
      return {
        ...state,
        currentSession: action.payload,
      };

    // ==========================================
    // FILTROS
    // ==========================================
    case ACTION_TYPES.SET_FILTERS:
      return {
        ...state,
        filters: {
          ...state.filters,
          ...action.payload,
        },
      };

    case ACTION_TYPES.RESET_FILTERS:
      return {
        ...state,
        filters: initialState.filters,
      };

    case ACTION_TYPES.SET_SELECTED_CHANNELS: {
      const next = new Set(action.payload || []);
      try {
        localStorage.setItem(
          "sc_selected_channels", JSON.stringify([...next]),
        );
      } catch {}
      return {
        ...state,
        selectedChannels: next,
        // Reset paginação ao mudar filtro de canal
        conversations: [],
        conversationsOffset: 0,
        hasMoreConversations: true,
      };
    }

    // ==========================================
    // UI STATE
    // ==========================================
    case ACTION_TYPES.SET_LOADING:
      return {
        ...state,
        loading: {
          ...state.loading,
          ...action.payload,
        },
      };

    case ACTION_TYPES.TOGGLE_CONTACT_PANEL:
      return {
        ...state,
        isContactPanelOpen:
          action.payload !== undefined
            ? action.payload
            : !state.isContactPanelOpen,
      };

    case ACTION_TYPES.TOGGLE_SIDEBAR:
      return {
        ...state,
        showSidebar:
          action.payload !== undefined ? action.payload : !state.showSidebar,
      };

    case ACTION_TYPES.SET_MOBILE:
      return {
        ...state,
        isMobile: action.payload,
      };

    // ==========================================
    // UNREAD COUNTS
    // ==========================================
    case ACTION_TYPES.SET_UNREAD_COUNT: {
      const { phone, count } = action.payload;
      return {
        ...state,
        unreadCounts: {
          ...state.unreadCounts,
          [phone]: count,
        },
      };
    }

    case ACTION_TYPES.RESET_UNREAD_COUNT: {
      const phone = action.payload;
      return {
        ...state,
        unreadCounts: {
          ...state.unreadCounts,
          [phone]: 0,
        },
      };
    }

    // ==========================================
    // RESET
    // ==========================================
    case ACTION_TYPES.RESET_STATE:
      return {
        ...initialState,
        isMobile: state.isMobile, // Manter configuração de mobile
      };

    default:
      return state;
  }
};

/**
 * Context para a Central de Atendimento
 */
const ServiceCenterContext = createContext(null);

/**
 * Provider Component
 */
export const ServiceCenterProvider = ({ children }) => {
  const [state, dispatch] = useReducer(serviceCenterReducer, initialState);

  // ==========================================
  // ACTIONS HELPERS
  // ==========================================

  // Contas
  const setAccounts = useCallback((accounts) => {
    dispatch({ type: ACTION_TYPES.SET_ACCOUNTS, payload: accounts });
  }, []);

  const setSelectedAccount = useCallback((account, profilePicture = null) => {
    dispatch({
      type: ACTION_TYPES.SET_SELECTED_ACCOUNT,
      payload: { account, profilePicture },
    });
  }, []);

  // Conversas
  const setConversations = useCallback((conversations, offset = 0, hasMore = true) => {
    dispatch({
      type: ACTION_TYPES.SET_CONVERSATIONS,
      payload: { conversations, offset, hasMore },
    });
  }, []);

  const addConversations = useCallback((conversations, offset, hasMore = true) => {
    dispatch({
      type: ACTION_TYPES.ADD_CONVERSATIONS,
      payload: { conversations, offset, hasMore },
    });
  }, []);

  const updateConversation = useCallback((conversation) => {
    dispatch({ type: ACTION_TYPES.UPDATE_CONVERSATION, payload: conversation });
  }, []);

  const setSelectedConversation = useCallback((conversation) => {
    dispatch({ type: ACTION_TYPES.SET_SELECTED_CONVERSATION, payload: conversation });
  }, []);

  // Contato
  const setSelectedContact = useCallback((contact) => {
    dispatch({ type: ACTION_TYPES.SET_SELECTED_CONTACT, payload: contact });
  }, []);

  const updateContact = useCallback((contact) => {
    dispatch({ type: ACTION_TYPES.UPDATE_CONTACT, payload: contact });
  }, []);

  // Mensagens
  const setMessages = useCallback((messages, offset = 0, hasMore = true) => {
    dispatch({
      type: ACTION_TYPES.SET_MESSAGES,
      payload: { messages, offset, hasMore },
    });
  }, []);

  const addOlderMessages = useCallback((messages, offset, hasMore = true) => {
    dispatch({
      type: ACTION_TYPES.ADD_MESSAGES,
      payload: { messages, offset, hasMore },
    });
  }, []);

  const addNewMessages = useCallback((messages) => {
    dispatch({ type: ACTION_TYPES.ADD_MESSAGE, payload: messages });
  }, []);

  const updateMessage = useCallback((messageId, updates) => {
    dispatch({
      type: ACTION_TYPES.UPDATE_MESSAGE,
      payload: { messageId, updates },
    });
  }, []);

  // Notas
  const setNotes = useCallback((notes) => {
    dispatch({ type: ACTION_TYPES.SET_NOTES, payload: notes });
  }, []);

  const addNote = useCallback((note) => {
    dispatch({ type: ACTION_TYPES.ADD_NOTE, payload: note });
  }, []);

  const updateNote = useCallback((note) => {
    dispatch({ type: ACTION_TYPES.UPDATE_NOTE, payload: note });
  }, []);

  const deleteNote = useCallback((noteId) => {
    dispatch({ type: ACTION_TYPES.DELETE_NOTE, payload: noteId });
  }, []);

  // Sessões
  const setSessions = useCallback((sessions) => {
    dispatch({ type: ACTION_TYPES.SET_SESSIONS, payload: sessions });
  }, []);

  const setCurrentSession = useCallback((session) => {
    dispatch({ type: ACTION_TYPES.SET_CURRENT_SESSION, payload: session });
  }, []);

  // Filtros
  const setSelectedChannels = useCallback((channels) => {
    dispatch({
      type: ACTION_TYPES.SET_SELECTED_CHANNELS,
      payload: Array.isArray(channels) ? channels : [...(channels || [])],
    });
  }, []);

  const setFilters = useCallback((filters) => {
    dispatch({ type: ACTION_TYPES.SET_FILTERS, payload: filters });
  }, []);

  const resetFilters = useCallback(() => {
    dispatch({ type: ACTION_TYPES.RESET_FILTERS });
  }, []);

  // UI
  const setLoading = useCallback((loadingState) => {
    dispatch({ type: ACTION_TYPES.SET_LOADING, payload: loadingState });
  }, []);

  const toggleContactPanel = useCallback((value) => {
    dispatch({ type: ACTION_TYPES.TOGGLE_CONTACT_PANEL, payload: value });
  }, []);

  const toggleSidebar = useCallback((value) => {
    dispatch({ type: ACTION_TYPES.TOGGLE_SIDEBAR, payload: value });
  }, []);

  const setMobile = useCallback((isMobile) => {
    dispatch({ type: ACTION_TYPES.SET_MOBILE, payload: isMobile });
  }, []);

  // Unread
  const setUnreadCount = useCallback((phone, count) => {
    dispatch({
      type: ACTION_TYPES.SET_UNREAD_COUNT,
      payload: { phone, count },
    });
  }, []);

  const incrementUnreadCount = useCallback((phone) => {
    const currentCount = state.unreadCounts[phone] || 0;
    dispatch({
      type: ACTION_TYPES.SET_UNREAD_COUNT,
      payload: { phone, count: currentCount + 1 },
    });
  }, [state.unreadCounts]);

  const resetUnreadCount = useCallback((phone) => {
    dispatch({ type: ACTION_TYPES.RESET_UNREAD_COUNT, payload: phone });
  }, []);

  // Reset
  const resetState = useCallback(() => {
    dispatch({ type: ACTION_TYPES.RESET_STATE });
  }, []);

  const value = {
    // State
    ...state,

    // Dispatch (para casos avançados)
    dispatch,

    // Actions
    setAccounts,
    setSelectedAccount,
    setConversations,
    addConversations,
    updateConversation,
    setSelectedConversation,
    setSelectedContact,
    updateContact,
    setMessages,
    addOlderMessages,
    addNewMessages,
    updateMessage,
    setNotes,
    addNote,
    updateNote,
    deleteNote,
    setSessions,
    setCurrentSession,
    setFilters,
    resetFilters,
    setSelectedChannels,
    setLoading,
    toggleContactPanel,
    toggleSidebar,
    setMobile,
    setUnreadCount,
    incrementUnreadCount,
    resetUnreadCount,
    resetState,
  };

  return (
    <ServiceCenterContext.Provider value={value}>
      {children}
    </ServiceCenterContext.Provider>
  );
};

/**
 * Hook para usar o contexto
 */
export const useServiceCenter = () => {
  const context = useContext(ServiceCenterContext);

  if (!context) {
    throw new Error(
      "useServiceCenter must be used within a ServiceCenterProvider"
    );
  }

  return context;
};

export default ServiceCenterContext;
